/**
 * dateUtils — 공용 날짜 유틸. DatePicker / DateRangePicker 양쪽에서 사용.
 *
 * 모든 날짜 문자열은 YYYY-MM-DD 형식. 사전순 비교 = 날짜순.
 */

export const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"] as const;

export function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface DateParts {
  y: number;
  m: number;
  d: number;
}

export function parseDate(s: string): DateParts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

export function fmtDate(y: number, m: number, d: number): string {
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

/** 해당 월의 시작 요일 (0=일) */
export function startWeekday(y: number, m: number): number {
  return new Date(y, m - 1, 1).getDay();
}

/** YYYY-MM-DD 문자열 비교 (사전순 = 날짜순) */
export function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** YYYY-MM-DD에서 N일 가감한 결과 (KST 안전) */
export function addDays(s: string, n: number): string {
  const p = parseDate(s);
  if (!p) return s;
  const d = new Date(Date.UTC(p.y, p.m - 1, p.d));
  d.setUTCDate(d.getUTCDate() + n);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** 6주 × 7일 캘린더 셀 생성 (전월 말일 → 이번 달 → 다음 달 초) */
export function buildCalendarCells(y: number, m: number): {
  y: number;
  m: number;
  d: number;
  outside: boolean;
}[] {
  const dim = daysInMonth(y, m);
  const startWd = startWeekday(y, m);
  const prevDim = daysInMonth(y, m === 1 ? 12 : m - 1);
  const prevY = m === 1 ? y - 1 : y;
  const prevM = m === 1 ? 12 : m - 1;
  const nextY = m === 12 ? y + 1 : y;
  const nextM = m === 12 ? 1 : m + 1;

  const cells: { y: number; m: number; d: number; outside: boolean }[] = [];
  for (let i = startWd - 1; i >= 0; i--) {
    cells.push({ y: prevY, m: prevM, d: prevDim - i, outside: true });
  }
  for (let d = 1; d <= dim; d++) {
    cells.push({ y, m, d, outside: false });
  }
  while (cells.length < 42) {
    const d = cells.length - (startWd + dim) + 1;
    cells.push({ y: nextY, m: nextM, d, outside: true });
  }
  return cells;
}
